import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Chip, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { listLookup } from '../../../api/masterData';
import {
  deleteStudentDocument,
  downloadStudentDocument,
  listStudentDocuments,
  uploadStudentDocument,
} from '../../../api/students';
import { downloadBlob } from '../../../utils/downloadBlob';

interface Step9Props {
  studentId: number | null;
}

const STUDENT_DOCUMENT_TYPE_NAMES = [
  'Photo',
  'NIC',
  'Passport',
  'Birth Certificate',
  'O/L Results',
  'A/L Results',
  'Degree Certificate',
  'IELTS',
  'CV',
  'Work Experience',
  'Recommendation Letter',
  'Offer Letter',
  'Visa',
  'Other',
];

const DocumentSlot = ({
  studentId,
  documentTypeId,
  label,
  latestFile,
  historyFiles,
  onChanged,
}: {
  studentId: number;
  documentTypeId: number;
  label: string;
  latestFile?: { id: number; originalName: string; version: number };
  historyFiles: Array<{ id: number; originalName: string; version: number }>;
  onChanged: () => void;
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadStudentDocument(studentId, file, documentTypeId),
    onSuccess: onChanged,
  });
  const deleteMutation = useMutation({
    mutationFn: (documentId: number) => deleteStudentDocument(documentId),
    onSuccess: onChanged,
  });

  const handleFiles = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (file) uploadMutation.mutate(file);
  };

  const handleDownload = async (documentId: number) => {
    const { blob, filename } = await downloadStudentDocument(documentId);
    downloadBlob(blob, filename);
  };

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      sx={{
        p: 2,
        border: '2px dashed',
        borderColor: dragOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: dragOver ? 'action.hover' : 'transparent',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">{label}</Typography>
        <Stack direction="row">
          {historyFiles.length > 0 && (
            <Tooltip title="Version history">
              <IconButton size="small" onClick={() => setShowHistory((v) => !v)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Upload new version">
            <IconButton size="small" onClick={() => inputRef.current?.click()} disabled={uploadMutation.isPending}>
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {!latestFile ? (
        <Typography variant="caption" color="text.secondary">
          Drag &amp; drop a file here, or click to browse.
        </Typography>
      ) : (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
            {latestFile.originalName} (v{latestFile.version})
          </Typography>
          <Tooltip title="Preview / download">
            <IconButton size="small" onClick={() => handleDownload(latestFile.id)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => deleteMutation.mutate(latestFile.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      <Collapse in={showHistory}>
        <Stack spacing={0.5} sx={{ mt: 1, pl: 3 }}>
          {historyFiles.map((f) => (
            <Stack key={f.id} direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                v{f.version} · {f.originalName}
              </Typography>
              <IconButton size="small" onClick={() => handleDownload(f.id)}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export const Step9Documents = ({ studentId }: Step9Props) => {
  const queryClient = useQueryClient();

  const documentTypesQuery = useQuery({
    queryKey: ['lookup', '/master-data/document-types'],
    queryFn: () => listLookup('/master-data/document-types', { activeOnly: true }),
  });
  const documentsQuery = useQuery({
    queryKey: ['student-documents', studentId, 'history'],
    queryFn: () => listStudentDocuments(studentId!, true),
    enabled: !!studentId,
  });

  if (!studentId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Complete Step 2 first — documents are uploaded directly against the student.
      </Typography>
    );
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['student-documents', studentId] });

  const relevantTypes = (documentTypesQuery.data ?? [])
    .filter((t) => STUDENT_DOCUMENT_TYPE_NAMES.includes(String(t.name)))
    .sort(
      (a, b) =>
        STUDENT_DOCUMENT_TYPE_NAMES.indexOf(String(a.name)) - STUDENT_DOCUMENT_TYPE_NAMES.indexOf(String(b.name)),
    );

  const allDocs = documentsQuery.data ?? [];
  const otherDocs = allDocs.filter(
    (d) => !d.documentTypeId || !relevantTypes.some((t) => t.id === d.documentTypeId),
  );

  return (
    <Stack spacing={2}>
      {relevantTypes.map((type) => {
        const forType = allDocs.filter((d) => d.documentTypeId === type.id).sort((a, b) => b.version - a.version);
        const latest = forType.find((d) => d.isLatest);
        const history = forType.filter((d) => !d.isLatest);
        return (
          <DocumentSlot
            key={type.id as number}
            studentId={studentId}
            documentTypeId={type.id as number}
            label={String(type.name)}
            latestFile={latest}
            historyFiles={history}
            onChanged={invalidate}
          />
        );
      })}

      {otherDocs.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Other uploaded files
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {otherDocs.map((d) => (
              <Chip key={d.id} size="small" label={d.originalName} icon={<InsertDriveFileOutlinedIcon />} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
